/**
 * Data Source Description Panel
 *
 * Data source nodes are type-only declarations - they don't carry a specific
 * instance (item/tracker/project ID), since that's chosen at run time via the
 * execution wizard instead. The only thing to configure here is the input's
 * name, which is what context_config sources (e.g. "scope.<name>.<field>")
 * and connected_inputs entries address it by - see client.ts's
 * `node.config?.name || sanitizeStepName(node.id)` fallback. Per item vs
 * aggregate lives on each outgoing edge instead (EdgeConfigSidebar.tsx).
 */

import React, { useState } from 'react';
import type { PipelineDefinition, PipelineNode } from '../types/pipeline';
import { isDataSourceNode, sanitizeStepName } from '../api/client';
import { getUpstreamNodes } from './ValueBindingEditor';
import { OutputPathPicker, useAgentOutputSchema } from './OutputPathPicker';
import './DataSourceConfig.css';

interface DataSourceConfigProps {
  node: PipelineNode;
  pipeline: PipelineDefinition | null;
  onSave: (config: { name: string; outline_depth?: number | null; value_source_field?: string | null }) => void;
  onClose: () => void;
}

const SOURCE_TYPE_LABELS: Record<string, string> = {
  data_source_item: 'Item',
  data_source_tracker: 'Tracker',
  data_source_project: 'Project',
  data_source_chapter: 'Chapter',
  data_source_tracker_schema: 'Tracker Schema',
  data_source_outline: 'Outline',
  data_source_tracker_list: 'Tracker List',
};

const ARTICLE: Record<string, string> = {
  Item: 'an',
  Tracker: 'a',
  Project: 'a',
  Chapter: 'a',
  'Tracker Schema': 'a',
  Outline: 'an',
  'Tracker List': 'a',
};

// Must match WorkflowInput.name's pattern in raiqon/schemas/workflow.py.
const NAME_PATTERN = /^[a-z][a-z0-9_]*$/;

export const DataSourceConfig: React.FC<DataSourceConfigProps> = ({ node, pipeline, onSave, onClose }) => {
  const sourceTypeLabel = SOURCE_TYPE_LABELS[node.task_type] || 'Unknown';
  const article = ARTICLE[sourceTypeLabel] || 'a';

  const [name, setName] = useState<string>(node.config?.name || sanitizeStepName(node.id));
  const isOutline = node.task_type === 'data_source_outline';
  // Empty means the full tree; otherwise how many layers below the tracker/chapter to include.
  const [outlineDepth, setOutlineDepth] = useState<string>(node.config?.outline_depth ? String(node.config.outline_depth) : '');
  const isTrackerList = node.task_type === 'data_source_tracker_list';

  // An edge from a step into this node means its value comes from that step's output at
  // run time (WorkflowInput.value_source) - the backend allows exactly one such step.
  const upstreamNodes = getUpstreamNodes(pipeline, node.id);
  const feedingNode = upstreamNodes[0];
  const isAgentFed = upstreamNodes.length > 0;
  const hasMultipleFeeders = upstreamNodes.length > 1;
  const [valueSourceField, setValueSourceField] = useState<string>(node.config?.value_source_field || 'response');
  const { schema: feedingSchema, loading: feedingSchemaLoading } = useAgentOutputSchema(feedingNode?.agent_id);

  const otherNames = new Set(
    (pipeline?.nodes || [])
      .filter(isDataSourceNode)
      .filter(n => n.id !== node.id)
      .map(n => n.config?.name || sanitizeStepName(n.id))
  );

  const trimmed = name.trim();
  const isValidPattern = NAME_PATTERN.test(trimmed);
  const isDuplicate = otherNames.has(trimmed);
  const isValidDepth = outlineDepth === '' || /^[1-9]\d*$/.test(outlineDepth);
  const canSave = isValidPattern && !isDuplicate && (!isOutline || isValidDepth) && !hasMultipleFeeders;

  const handleSave = () => {
    if (!canSave) return;
    onSave({
      ...(isOutline ? { name: trimmed, outline_depth: outlineDepth === '' ? null : Number(outlineDepth) } : { name: trimmed }),
      value_source_field: isAgentFed ? valueSourceField || 'response' : null,
    });
    onClose();
  };

  return (
    <div className="data-source-config">
      <div className="data-source-header">
        <h3>{node.label}</h3>
        <button className="close-button" onClick={onClose}>×</button>
      </div>

      <div className="data-source-body">
        <p className="help-text">
          Declares that this workflow expects {article} {sourceTypeLabel.toLowerCase()} as input.
          {isAgentFed ? (
            <> The specific {sourceTypeLabel.toLowerCase()} comes from an upstream agent's output (see below).</>
          ) : (
            <>
              {' '}The specific {sourceTypeLabel.toLowerCase()} to run against is chosen when the
              workflow is executed, not here.
            </>
          )}
        </p>

        {isAgentFed && (
          <div className="condition-row">
            <label>Value from agent</label>
            {hasMultipleFeeders ? (
              <p className="field-error">Only one agent can feed an input - remove the extra incoming edges.</p>
            ) : (
              <>
                <p className="help-text">
                  This input is filled in at run time from {feedingNode.label || feedingNode.id}'s output, not in
                  the run dialog. The agent must run in aggregate mode and answer with an id, URL, or a{' '}
                  <code>[id] Name</code> line.
                </p>
                <OutputPathPicker
                  value={valueSourceField}
                  onChange={setValueSourceField}
                  schema={feedingSchema}
                  loading={feedingSchemaLoading}
                  includeRawResponse={true}
                />
              </>
            )}
          </div>
        )}

        <div className="condition-row">
          <label>Input Name</label>
          <input
            type="text"
            className="condition-input"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="e.g. chapter"
          />
          {!isValidPattern && trimmed !== '' && (
            <p className="field-error">Must start with a lowercase letter and contain only lowercase letters, numbers, and underscores.</p>
          )}
          {isDuplicate && (
            <p className="field-error">Another data source node already uses this name.</p>
          )}
          <p className="help-text">
            Reference this input elsewhere as <code>context_config</code> source{' '}
            <code>scope.{trimmed || '<name>'}.&lt;field&gt;</code>, or in <code>connected_inputs</code>.
          </p>
        </div>

        {isOutline ? (
          <div className="condition-row">
            <label>Depth</label>
            <input
              type="number"
              min={1}
              className="condition-input"
              value={outlineDepth}
              onChange={e => setOutlineDepth(e.target.value)}
              placeholder="Full tree"
            />
            {!isValidDepth && <p className="field-error">Must be a whole number of at least 1, or empty for the full tree.</p>}
            <p className="help-text">
              How many layers below the tracker or chapter to include - 1 is just the direct children.
              Leave empty for the full tree. Render it with <code>scope.{trimmed || '<name>'}.outline</code>{' '}
              (indented tree) or <code>scope.{trimmed || '<name>'}.json</code>. Siblings are in no particular order.
            </p>
          </div>
        ) : isTrackerList ? (
          <p className="help-text">
            Resolves to every tracker in the project - names and ids, no items. Render it with{' '}
            <code>scope.{trimmed || '<name>'}.trackers</code> (one <code>- [id] name</code> line per tracker) or{' '}
            <code>scope.{trimmed || '<name>'}.json</code>. An agent can answer with one of those lines to pick a
            tracker for a downstream Tracker input.
          </p>
        ) : (
          <p className="help-text">
            Whether a step loops over this source or sees all of it at once is set per
            connection - click the edge between this node and the step.
          </p>
        )}
      </div>

      <div className="config-footer">
        <button className="button-secondary" onClick={onClose}>Cancel</button>
        <button className="button-primary" onClick={handleSave} disabled={!canSave}>Save</button>
      </div>
    </div>
  );
};
